import React, { useEffect, useRef, useState } from "react";

/**
 * Icon-button menu with arrow-key navigation; Escape or Tab closes it and a click outside dismisses it.
 * Unstyled: it renders the classes `mw`, `ib`, `menu` and `d` (danger item) for the screen's stylesheet to target.
 * Items are { icon, label, danger, onSelect } where `icon` is a node; `triggerIcon` is the button's icon node.
 * Two non-action entries lay out sections: { separator: true } draws a rule and { heading: "Move to" } a small label
 * (class `mh`); arrow keys skip them.
 */
export default function KebabMenu({ label, items, triggerIcon }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    const menu = menuRef.current;
    if (!open || !menu) return undefined;
    const buttons = () => [...menu.querySelectorAll("[role=menuitem]")];
    buttons()[0]?.focus();
    const onKey = (e) => {
      const list = buttons();
      const i = list.indexOf(document.activeElement);
      if (e.key === "ArrowDown") { e.preventDefault(); list[(i + 1) % list.length].focus(); }
      if (e.key === "ArrowUp") { e.preventDefault(); list[(i - 1 + list.length) % list.length].focus(); }
      if (e.key === "Escape") { setOpen(false); triggerRef.current?.focus(); }
      if (e.key === "Tab") setOpen(false);
    };
    const onDown = (e) => { if (!menu.parentNode.contains(e.target)) setOpen(false); };
    menu.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => { menu.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onDown); };
  }, [open]);

  return (
    <span className="mw">
      <button type="button" ref={triggerRef} className="ib" aria-haspopup="menu" aria-expanded={open} aria-label={label} onClick={() => setOpen(!open)}>
        {triggerIcon}
      </button>
      {open && (
        <div className="menu" role="menu" ref={menuRef}>
          {items.map((it, i) => {
            if (it.separator) return <hr key={`sep-${i}`} />;
            if (it.heading) return <div key={`head-${i}`} className="mh" role="presentation">{it.heading}</div>;
            return (
              <button key={it.label} type="button" role="menuitem" className={it.danger ? "d" : ""} onClick={() => { setOpen(false); it.onSelect(); }}>
                {it.icon}{it.label}
              </button>
            );
          })}
        </div>
      )}
    </span>
  );
}
